// Vocabulário compartilhado: estados, papéis, ações genéricas e erros.
// Glossário dos três idiomas em docs/design/i18n.md.
import { defineMessages, plural } from "./core";

export const common = defineMessages({
  "pt-BR": {
    loading: "Carregando",
    loadError: "Não foi possível carregar. Verifique a conexão e tente de novo.",
    networkError: "Sem conexão com o servidor. Verifique a rede ou a VPN.",
    serverError: (status: number) => `Erro ${status} do servidor.`,
    copy: "Copiar",
    copyLabel: (label: string) => `Copiar ${label}`,
    copied: "Copiado",
    cancel: "Cancelar",
    save: "Salvar",
    close: "Fechar",
    back: "Voltar",
    today: "hoje",
    language: "Idioma",
    languageSaveError: "Não foi possível salvar o idioma na sua conta; ele vale só neste navegador.",
    role: { platform_admin: "Admin da plataforma", org_admin: "Admin da organização", operator: "Operador", viewer: "Leitor" },
    protection: { protected: "Protegida", stale: "Atrasada", failed: "Falhou", never: "Sem backup", revoked: "Revogada" },
    jobKind: { backup: "Backup", image: "Imagem de disco", database: "Backup de banco", dbtest: "Teste de banco", share: "Backup de compartilhamento", sharetest: "Teste de compartilhamento", restore: "Restauração", replication: "Replicação", maintenance: "Manutenção", enrollment: "Registro" },
    jobStatus: { queued: "Na fila", running: "Em andamento", succeeded: "Concluído", warning: "Concluído com avisos", failed: "Falhou" },
    poolHealth: { healthy: "Saudável", degraded: "Degradado", unreachable: "Inacessível" },
    backend: {
      local: "Disco local (datacenter)", s3: "Amazon S3 ou compatível", azure: "Azure Blob Storage", gcs: "Google Cloud Storage",
      b2: "Backblaze B2", sftp: "SFTP", webdav: "WebDAV", interserver: "InterServer Storage (API)",
    },
    retentionKind: { latest: "mais recente", daily: "diário", weekly: "semanal", monthly: "mensal", yearly: "anual", pinned: "fixado" },
    dayState: { ok: "backup concluído", partial: "concluído com arquivos ignorados", failed: "falhou", none: "sem backup" },
    dayLegend: { ok: "Concluído", partial: "Parcial", failed: "Falhou", none: "Sem backup" },
    stripSummary: (ok: number, total: number, failed: number) =>
      `${ok} de ${total} dias com ponto de restauração` + (failed ? `, ${failed} com falha` : ""),
    weekdaysShort: ["dom", "seg", "ter", "qua", "qui", "sex", "sáb"],
    schedule: {
      hourly: (minute: string) => `A cada hora, a partir de :${minute}`,
      everyDay: "todos os dias",
      weekdays: "seg a sex",
      weekly: (days: string, time: string, window: number) => `Semanal (${days}) às ${time}, janela de ${window} h`,
      daily: (days: string, time: string, window: number) => `Diário às ${time}, ${days}, janela de ${window} h`,
    },
    retention: {
      daily: (n: number) => `${n} ${plural(n, "diário", "diários")}`,
      weekly: (n: number) => `${n} ${plural(n, "semanal", "semanais")}`,
      monthly: (n: number) => `${n} ${plural(n, "mensal", "mensais")}`,
      yearly: (n: number) => `${n} ${plural(n, "anual", "anuais")}`,
      none: "sem retenção",
      years: (n: number) => `${n} ${plural(n, "ano", "anos")}`,
      yearsMonths: (y: number, m: number) => `${y} ${plural(y, "ano", "anos")} e ${m} ${plural(m, "mês", "meses")}`,
      months: (n: number) => `${n} ${plural(n, "mês", "meses")}`,
      weeks: (n: number) => `${n} ${plural(n, "semana", "semanas")}`,
      days: (n: number) => `${n} ${plural(n, "dia", "dias")}`,
    },
  },
  "en-US": {
    loading: "Loading",
    loadError: "Could not load. Check your connection and try again.",
    networkError: "No connection to the server. Check your network or VPN.",
    serverError: (status: number) => `Server error ${status}.`,
    copy: "Copy",
    copyLabel: (label: string) => `Copy ${label}`,
    copied: "Copied",
    cancel: "Cancel",
    save: "Save",
    close: "Close",
    back: "Back",
    today: "today",
    language: "Language",
    languageSaveError: "Could not save the language to your account; it applies to this browser only.",
    role: { platform_admin: "Platform admin", org_admin: "Organization admin", operator: "Operator", viewer: "Viewer" },
    protection: { protected: "Protected", stale: "Overdue", failed: "Failed", never: "No backup", revoked: "Revoked" },
    jobKind: { backup: "Backup", image: "Disk image", database: "Database backup", dbtest: "Database test", share: "Share backup", sharetest: "Share test", restore: "Restore", replication: "Replication", maintenance: "Maintenance", enrollment: "Enrollment" },
    jobStatus: { queued: "Queued", running: "Running", succeeded: "Completed", warning: "Completed with warnings", failed: "Failed" },
    poolHealth: { healthy: "Healthy", degraded: "Degraded", unreachable: "Unreachable" },
    backend: {
      local: "Local disk (datacenter)", s3: "Amazon S3 or compatible", azure: "Azure Blob Storage", gcs: "Google Cloud Storage",
      b2: "Backblaze B2", sftp: "SFTP", webdav: "WebDAV", interserver: "InterServer Storage (API)",
    },
    retentionKind: { latest: "latest", daily: "daily", weekly: "weekly", monthly: "monthly", yearly: "yearly", pinned: "pinned" },
    dayState: { ok: "backup completed", partial: "completed with skipped files", failed: "failed", none: "no backup" },
    dayLegend: { ok: "Completed", partial: "Partial", failed: "Failed", none: "No backup" },
    stripSummary: (ok: number, total: number, failed: number) =>
      `${ok} of ${total} days with a restore point` + (failed ? `, ${failed} failed` : ""),
    weekdaysShort: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
    schedule: {
      hourly: (minute: string) => `Every hour, at :${minute}`,
      everyDay: "every day",
      weekdays: "Mon to Fri",
      weekly: (days: string, time: string, window: number) => `Weekly (${days}) at ${time}, ${window} h window`,
      daily: (days: string, time: string, window: number) => `Daily at ${time}, ${days}, ${window} h window`,
    },
    retention: {
      daily: (n: number) => `${n} daily`,
      weekly: (n: number) => `${n} weekly`,
      monthly: (n: number) => `${n} monthly`,
      yearly: (n: number) => `${n} yearly`,
      none: "no retention",
      years: (n: number) => `${n} ${plural(n, "year", "years")}`,
      yearsMonths: (y: number, m: number) => `${y} ${plural(y, "year", "years")} and ${m} ${plural(m, "month", "months")}`,
      months: (n: number) => `${n} ${plural(n, "month", "months")}`,
      weeks: (n: number) => `${n} ${plural(n, "week", "weeks")}`,
      days: (n: number) => `${n} ${plural(n, "day", "days")}`,
    },
  },
  es: {
    loading: "Cargando",
    loadError: "No se pudo cargar. Revisa la conexión e inténtalo de nuevo.",
    networkError: "Sin conexión con el servidor. Revisa la red o la VPN.",
    serverError: (status: number) => `Error ${status} del servidor.`,
    copy: "Copiar",
    copyLabel: (label: string) => `Copiar ${label}`,
    copied: "Copiado",
    cancel: "Cancelar",
    save: "Guardar",
    close: "Cerrar",
    back: "Volver",
    today: "hoy",
    language: "Idioma",
    languageSaveError: "No se pudo guardar el idioma en tu cuenta; solo se aplica en este navegador.",
    role: { platform_admin: "Admin de la plataforma", org_admin: "Admin de la organización", operator: "Operador", viewer: "Lector" },
    protection: { protected: "Protegido", stale: "Atrasado", failed: "Falló", never: "Sin copia", revoked: "Revocado" },
    jobKind: { backup: "Copia de seguridad", image: "Imagen de disco", database: "Copia de base de datos", dbtest: "Prueba de base de datos", share: "Copia de recurso compartido", sharetest: "Prueba de recurso compartido", restore: "Restauración", replication: "Replicación", maintenance: "Mantenimiento", enrollment: "Registro" },
    jobStatus: { queued: "En cola", running: "En curso", succeeded: "Completado", warning: "Completado con avisos", failed: "Falló" },
    poolHealth: { healthy: "Correcto", degraded: "Degradado", unreachable: "Inaccesible" },
    backend: {
      local: "Disco local (datacenter)", s3: "Amazon S3 o compatible", azure: "Azure Blob Storage", gcs: "Google Cloud Storage",
      b2: "Backblaze B2", sftp: "SFTP", webdav: "WebDAV", interserver: "InterServer Storage (API)",
    },
    retentionKind: { latest: "más reciente", daily: "diario", weekly: "semanal", monthly: "mensual", yearly: "anual", pinned: "fijado" },
    dayState: { ok: "copia completada", partial: "completada con archivos omitidos", failed: "falló", none: "sin copia" },
    dayLegend: { ok: "Completada", partial: "Parcial", failed: "Falló", none: "Sin copia" },
    stripSummary: (ok: number, total: number, failed: number) =>
      `${ok} de ${total} días con punto de restauración` + (failed ? `, ${failed} con falla` : ""),
    weekdaysShort: ["dom", "lun", "mar", "mié", "jue", "vie", "sáb"],
    schedule: {
      hourly: (minute: string) => `Cada hora, a partir de :${minute}`,
      everyDay: "todos los días",
      weekdays: "lun a vie",
      weekly: (days: string, time: string, window: number) => `Semanal (${days}) a las ${time}, ventana de ${window} h`,
      daily: (days: string, time: string, window: number) => `Diaria a las ${time}, ${days}, ventana de ${window} h`,
    },
    retention: {
      daily: (n: number) => `${n} ${plural(n, "diaria", "diarias")}`,
      weekly: (n: number) => `${n} ${plural(n, "semanal", "semanales")}`,
      monthly: (n: number) => `${n} ${plural(n, "mensual", "mensuales")}`,
      yearly: (n: number) => `${n} ${plural(n, "anual", "anuales")}`,
      none: "sin retención",
      years: (n: number) => `${n} ${plural(n, "año", "años")}`,
      yearsMonths: (y: number, m: number) => `${y} ${plural(y, "año", "años")} y ${m} ${plural(m, "mes", "meses")}`,
      months: (n: number) => `${n} ${plural(n, "mes", "meses")}`,
      weeks: (n: number) => `${n} ${plural(n, "semana", "semanas")}`,
      days: (n: number) => `${n} ${plural(n, "día", "días")}`,
    },
  },
});
